import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { useContext, useEffect, useState } from 'react';

import { SpotterLocation } from '@/common/types';
import { mapInfoBlue } from '@/theme';
import { lngLatToTurf } from '@/utils';

export type TrackLineLayerProps = {
    trackPositions?: SpotterLocation[];
};

const lineId = 'spotter-track-line';
const sourceId = 'spotter-track-line-source';

const EMPTY_FEATURE = {
    type: 'Feature' as const,
    properties: {},
    geometry: {
        type: 'LineString' as const,
        coordinates: [],
    },
};

export const TrackLineLayer = ({ trackPositions }: TrackLineLayerProps) => {
    const { map } = useContext(MapboxContext);
    const [trackDataSource, setTrackDataSource] = useState<mapboxgl.GeoJSONSource>();

    useEffect(() => {
        if (!map) {
            return;
        }

        map.addSource(sourceId, {
            type: 'geojson',
            data: EMPTY_FEATURE,
        });

        map.addLayer({
            id: lineId,
            type: 'line',
            source: sourceId,
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': mapInfoBlue,
                'line-width': 1.25,
                'line-dasharray': [1, 2], // Creates dotted line
            },
        });

        setTrackDataSource(map.getSource(sourceId) as mapboxgl.GeoJSONSource);
    }, [map]);

    useEffect(() => {
        if (!map || !trackPositions || !trackDataSource) {
            return;
        }

        if (trackPositions.length < 2) {
            trackDataSource.setData(EMPTY_FEATURE);
            return;
        }

        const coordinates = trackPositions.map(lngLatToTurf);
        const lineGeoJSON = {
            type: 'Feature' as const,
            properties: {},
            geometry: {
                type: 'LineString' as const,
                coordinates,
            },
        };

        trackDataSource.setData(lineGeoJSON);
    }, [map, trackPositions, trackDataSource]);

    return null;
};
